import { useState } from 'react'
import { Box, Tooltip } from '@mui/material'
import { Organization } from '@/types/organizations'
import { useAuthentication } from '@/components/auth/AuthProvider'
import useOverlay from '@/hooks/useOverlay'
import LabeledData from '@/components/labeled-data/LabeledData'
import ReadOnlyOrganizationDialog from './ReadOnlyOrganizationDialog'
import OrganizationPickerWithDialog from './OrganizationPickerWithDialog'
import OrganizationWithParent from './OrganizationWithParent'
import useOrganizationTypeName from './useOrganizationTypeName'

type OrganizationAssignmentSelectProps = {
  isFetching: boolean
  selectedOrganization: Organization
  organizations: Organization[]
  onChange: (organization: Organization) => void
  rootTreeOrgId: string
  dense?: boolean
  disabled?: boolean
  isBulkChange?: boolean
  showParentOrg?: boolean
  variant?: 'standard' | 'outlined'
}

export default function OrganizationAssignmentSelect({
  isFetching,
  selectedOrganization,
  organizations,
  onChange,
  dense,
  disabled,
  isBulkChange = false,
  showParentOrg = false,
  rootTreeOrgId,
  variant = 'standard',
}: OrganizationAssignmentSelectProps) {
  const { user } = useAuthentication()
  const readOnlyOverlay = useOverlay()
  const [readOnlyOrg, setReadOnlyOrg] = useState<Organization | null>(null)

  const cantEdit =
    disabled ||
    (selectedOrganization.id !== 'multiple' &&
      !user?.resolved_permission_codes.includes('*superuser*') &&
      !user?.read_write_org_ids.includes(selectedOrganization.id))

  const organizationTypeName = useOrganizationTypeName({
    organization: selectedOrganization,
  })

  return (
    <>
      {cantEdit ? (
        <Tooltip
          arrow
          enterDelay={1000}
          title="Read-Only Access"
          disableInteractive
        >
          <Box
            sx={{
              px: 1,
              pb: 0.5,
              pt: 1,
              borderRadius: 2,
            }}
          >
            {showParentOrg ? (
              <OrganizationWithParent
                organization={selectedOrganization}
                organizations={organizations}
                dense={dense}
              />
            ) : (
              <LabeledData
                label={organizationTypeName}
                data={selectedOrganization.name}
                dense={dense}
              />
            )}
          </Box>
        </Tooltip>
      ) : (
        <OrganizationPickerWithDialog
          currentOrganization={selectedOrganization}
          dense={dense}
          isFetching={isFetching}
          organizations={organizations}
          rootTreeOrgId={rootTreeOrgId}
          showParentOrg={showParentOrg}
          title="Assign to Organization"
          onSave={(organization) => {
            if (!user?.read_write_org_ids.includes(organization.id)) {
              setReadOnlyOrg(organization)
              readOnlyOverlay.open()
            } else {
              onChange(organization)
            }
          }}
          variant={variant}
        />
      )}

      {readOnlyOrg && (
        <ReadOnlyOrganizationDialog
          overlay={readOnlyOverlay}
          onContinue={() => {
            onChange(readOnlyOrg)
          }}
          onCancel={() => setReadOnlyOrg(null)}
          isBulkChange={isBulkChange}
        />
      )}
    </>
  )
}
